import { defineStore, acceptHMRUpdate } from 'pinia'
import { ref } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'
import { useQueryClient } from '@tanstack/vue-query'

import { log } from 'src/services/app/log.service'
import { authService } from 'src/services/api/auth.service'
import { usePostHog } from 'src/composables/app/ui/usePostHog'
import { auth0LogoutRedirectUri } from 'src/config'
import { ACCESS_TOKEN_FAILURE, API_ERRORS, ERROR_TYPES } from 'src/services/consts/api.const'
import { BROADCAST_TOPICS } from 'src/services/consts/app.const'
import { broadcastService } from 'src/services/app/broadcast.service'
import { shouldRefreshAccessToken } from 'src/services/helpers/auth.helper'
import { isValidRedirectRoute } from 'src/services/helpers/security.helper'
import { useAccountStore } from 'src/stores/account'
import { resetStores } from 'src/stores/reset.store'

export const useAuthStore = defineStore('auth', () => {
  const { reset, captureException } = usePostHog()
  const queryClient = useQueryClient()
  const {
    user: auth0User,
    error,
    isLoading,
    isAuthenticated,
    logout: auth0Logout,
    loginWithRedirect: auth0Login,
    getAccessTokenSilently,
  } = useAuth0()

  const isLoggingOut = ref(false)

  /**
   * Full logout: invalidate backend session (when applicable), clear app state,
   * then federated Auth0 logout via /v2/logout (clears Auth0 SSO cookies and
   * the SDK localStorage token cache). Auth0 redirects to `returnTo` when it is
   * a valid relative path (e.g. invite landing with token); otherwise the
   * configured logout URI / origin. The route guard then sends unauthenticated
   * users to Universal Login unless they landed on a public route.
   */
  const logout = async ({ fromOtherTab = false, authError = false, returnTo } = {}) => {
    const defaultReturnTo = auth0LogoutRedirectUri || window.location.origin
    // Optional relative path only — invalid values fall back silently.
    const resolvedReturnTo =
      returnTo != null && isValidRedirectRoute(returnTo)
        ? new URL(returnTo, defaultReturnTo).href
        : defaultReturnTo
    let redirectStarted = false

    try {
      isLoggingOut.value = true

      // 1. Invalidate backend session first (user-initiated logout only).
      if (!(authError || fromOtherTab)) {
        await authService.logout()
      }

      // 2. Cross-tab + analytics only — defer query/storage teardown until redirect.
      if (!fromOtherTab) {
        broadcastService.post(BROADCAST_TOPICS.LOGOUT)
      }
      broadcastService.closeChannel()
      reset()

      // 3. Federated logout — hits Auth0 /v2/logout, clears server-side session
      // cookies (openUrl: false only clears local cache and leaves SSO intact).
      await auth0Logout({
        logoutParams: {
          returnTo: resolvedReturnTo,
        },
        openUrl(url) {
          redirectStarted = true
          queryClient.clear()
          resetStores()
          window.location.replace(url)
        },
      })
    } catch (err) {
      queryClient.clear()
      resetStores()
      captureException(err, {
        error_type: ERROR_TYPES.AUTH,
        error_name: `[${API_ERRORS.LOGOUT_AUTH_ERROR}] ${err.message}`,
        original_error_message: err.message,
        error_context: 'Auth: logout',
        from_other_tab: fromOtherTab,
        auth_error: authError,
      })
      throw err
    } finally {
      if (!redirectStarted) {
        isLoggingOut.value = false
      }
    }
  }

  const login = async ({ appState, authorizationParams } = {}) => {
    try {
      // loginWithRedirect bypasses the SDK __proxy that clears error on success —
      // reset stale callback errors so the gate shows AuthLoadingPanel before redirect.
      error.value = null
      // authorizationParams lets the invite landing pass a `login_hint` so Auth0
      // pre-fills the invited email (linking depends on that exact address).
      await auth0Login({ appState, ...(authorizationParams && { authorizationParams }) })
    } catch (err) {
      log.error('Login error:', err)
      captureException(err, {
        error_type: ERROR_TYPES.AUTH,
        error_name: `[${API_ERRORS.LOGIN_AUTH_ERROR}] ${err.message}`,
        original_error_message: err.message,
        error_context: 'Auth: login',
      })
      throw err
    }
  }

  const retryAuth = async () => {
    if (isAuthenticated.value) {
      const accountStore = useAccountStore()

      // A /me 401 can mean the cached access token predates a claim change
      // (e.g. email_verified), so retrying with it would fail identically.
      // Failing to mint a fresh one still falls through to the refetch, so
      // retry is never worse than before.
      try {
        await getAccessTokenSilently({ cacheMode: 'off' })
      } catch (err) {
        log.error('Retry auth: could not refresh access token:', err)
      }

      await accountStore.refetch()
      return
    }

    window.location.assign(window.location.origin)
  }

  /**
   * Access token for API Bearer auth.
   * The Auth0 SDK refreshes from the token response's expires_in. That clock can
   * disagree with the JWT exp, so a returned token inside the same 60s window the
   * SDK uses is discarded and fetched once with the cache off. An opaque token
   * has no exp to read, so the SDK's own expires_in check stands.
   * An Auth0 rejection is rethrown as the SDK sent it. A token we still will not
   * send throws with ACCESS_TOKEN_FAILURE.
   */
  const getAccessToken = async () => {
    let token = await getAccessTokenSilently()
    if (token && !shouldRefreshAccessToken(token)) return token

    token = await getAccessTokenSilently({ cacheMode: 'off' })
    if (token && !shouldRefreshAccessToken(token)) return token

    const error = new Error('Session expired - unable to refresh access token')
    error.authReason = token ? ACCESS_TOKEN_FAILURE.NOT_USABLE : ACCESS_TOKEN_FAILURE.MISSING
    throw error
  }

  return {
    auth0User,
    error,
    isLoading,
    isAuthenticated,
    isLoggingOut,

    getAccessToken,
    login,
    logout,
    retryBootstrap: retryAuth,
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot))
}
